import React, { useCallback, useState } from 'react';
import {
    View,
    TouchableOpacity,
    Modal,
    ActivityIndicator,
    Image,
    ScrollView,
    Switch,
} from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import { WindowInformation } from '../../../services/window-information';
import { MoreThanOneFaceOnPhoto, NoFaceOnPhoto, PersonType, facialService } from '../../../services/central-api/facial';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CustomButton from '../../../components/CustomButton';
import FormInput from '../../../components/FormInput';
import MyCpfInput from '../../../components/MyCpfInput';
import TagsDropdown from '../../../components/facial/TagsDropdown';
import PersonTypeDropdown from '../../../components/facial/PersonTypeDropdown';
import { ClientError } from '../../../services/central-api/base-service';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import PersonFormPictures from './PersonFormPictures';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';

interface PersonFormParams {
    navigation: Navigation;
    route: {
        params?: {
            id: number;
        };
    };
}

export default function PersonForm({ navigation, route }: PersonFormParams) {
    const { styles, theme } = useStyles(stylesheet);

    const windowInfo = WindowInformation();

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [imageZoom, setImageZoom] = useState<FacialFacePicture>();
    const [files, setFiles] = useState<{ file: File; objectURI: string; key: string; }[]>([]);
    const [isDeleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
    const [isDeleteFacePictureModalVisible, setDeleteFacePictureModalVisible] = useState<boolean>(false);
    const [facePictureToDelete, setFacePictureToDelete] = useState<FacialFacePicture>();
    const [isCpfInvalid, setCpfInvalid] = useState(false);
    const [isSspWantedPerson, setIsSspWantedPerson] = useState(false);

    // person props
    const [comment, setComment] = useState('');
    const [name, setName] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [nickname, setNickname] = useState('');
    const [document, setDocument] = useState('');
    const [personMarkers, setPersonMarkers] = useState<number[]>([]);
    const [personType, setPersonType] = useState<PersonType | ''>('');
    const [facePictures, setFacePictures] = useState<FacialFacePicture[]>([]);
    const [register, setRegister] = useState('');
    const [state, setState] = useState('');
    const [city, setCity] = useState('');

    // lawsuit props
    const [arrestWarrantNumber, setArrestWarrantNumber] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const [issuingBody, setIssuingBody] = useState('');
    const [lawsuitCity, setLawsuitCity] = useState('');
    const [lawsuitNumber, setLawsuitNumber] = useState('');
    const [magistrate, setMagistrate] = useState('');
    const [prisonKind, setPrisonKind] = useState('');
    const [shippingDate, setShippingDate] = useState('');
    const [situation, setSituation] = useState('');

    async function getPerson(id: number) {
        try {
            setIsLoading(true);
            const person = await facialService.getFacialPerson(id);

            // person props
            setComment(person.comment || '');
            setIsActive(person.isActive);
            setName(person.name);
            setNickname(person.nickname || '');
            setDocument(person.document || '');
            setRegister(person.register || '');
            setState(person.state || '');
            setCity(person.city || '');
            setPersonMarkers(person.markers.map(p => p.id || 0));
            setFacePictures(person.facePictures);
            setPersonType(person.personType as PersonType);
            setIsSspWantedPerson(person.sspWantedPersonId ? true : false);

            // lawsuit props
            setArrestWarrantNumber(person.lawsuits[0].arrestWarrantNumber || '');
            setExpirationDate(person.lawsuits[0].expirationDate || '');
            setIssuingBody(person.lawsuits[0].issuingBody || '');
            setLawsuitCity(person.lawsuits[0].lawsuitCity || '');
            setLawsuitNumber(person.lawsuits[0].lawsuitNumber || '');
            setMagistrate(person.lawsuits[0].magistrate || '');
            setPrisonKind(person.lawsuits[0].prisonKind || '');
            setShippingDate(person.lawsuits[0].shippingDate || '');
            setSituation(person.lawsuits[0].situation || '');

        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            if (route.params?.id) {
                getPerson(route.params.id);
            }
        }
    }, [isFocused, navigation, route]));

    async function deletePerson(personId: number) {
        try {
            setIsDeleting(true);
            await facialService.deletePerson(personId);
            navigation.navigate('PeopleList');
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsDeleting(false);
        }
    }

    async function deleteFacePicture(facePicture: FacialFacePicture) {
        try {
            await facialService.deleteFacePicture({
                personId: route.params?.id || 0,
                facePictureId: facePicture.id
            });

            const index = facePictures.indexOf(facePicture);
            if (index !== -1) {
                facePictures.splice(index, 1);
            }
            setFacePictures([...facePictures]);

        } catch (err) {
            console.error(err);
        }
    }

    async function save() {
        try {
            setIsSaving(true);

            if (!name || !personType) {
                return;
            }

            let personId: number;

            if (route.params?.id) {
                personId = route.params?.id;
                await facialService.updatePerson(route.params.id, {
                    name,
                    isActive,
                    nickname,
                    document,
                    comment,
                    register,
                    state,
                    city,
                    arrestWarrantNumber,
                    expirationDate,
                    issuingBody,
                    lawsuitCity,
                    lawsuitNumber,
                    magistrate,
                    prisonKind,
                    shippingDate,
                    situation,
                    markers: personMarkers,
                    allowListGroupsIds: []
                });
            } else {
                const res = await facialService.addPerson({
                    name,
                    isActive,
                    nickname,
                    document,
                    comment,
                    register,
                    state,
                    city,
                    arrestWarrantNumber,
                    expirationDate,
                    issuingBody,
                    lawsuitCity,
                    lawsuitNumber,
                    magistrate,
                    prisonKind,
                    shippingDate,
                    situation,
                    personType,
                    markers: personMarkers,
                    allowListGroupsIds: []
                });
                personId = res.id || 0;
            }

            for (const file of files) {
                await facialService.addFacePicture({
                    personId,
                    photo: file.file
                });
            }

            cleanValues();
            navigation.navigate('PeopleList');
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            if (err instanceof NoFaceOnPhoto) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            if (err instanceof MoreThanOneFaceOnPhoto) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsSaving(false);
        }
    }

    function cleanValues() {
        // person props
        setComment('');
        setIsActive(true);
        setName('');
        setNickname('');
        setDocument('');
        setRegister('');
        setState('');
        setCity('');
        setPersonMarkers([]);
        setFacePictures([]);
        setPersonType('');

        // lawsuit props
        setArrestWarrantNumber('');
        setExpirationDate('');
        setIssuingBody('');
        setLawsuitCity('');
        setLawsuitNumber('');
        setMagistrate('');
        setPrisonKind('');
        setShippingDate('');
        setSituation('');
    }

    return <Container>
        <Header
            title={translate('registerPerson')}
            goBackAction={() => {
                cleanValues();
                navigation.canGoBack() ? navigation.goBack() : navigation.navigate('PeopleList');
            }}
            actions={!isSspWantedPerson && <>
                {route.params?.id && <View style={{ justifyContent: 'flex-end' }}>
                    <CustomButton
                        text={translate('delete')}
                        onPress={() => setDeleteModalVisible(true)}
                        type='secondary'
                        disabled={isLoading || isSaving}
                        isLoading={isDeleting}
                    />
                </View>}

                <View style={{ justifyContent: 'flex-end' }}>
                    <CustomButton
                        text={translate('save')}
                        onPress={save}
                        disabled={isLoading || isDeleting || !name || !personType || isCpfInvalid}
                        isLoading={isSaving}
                    />
                </View>
            </>}
        />
        <Card>
            <ScrollView contentContainerStyle={{ padding: 24, gap: 10 }}>
                {isLoading &&
                    <View style={{ paddingTop: 20 }}>
                        <ActivityIndicator size='small' color={theme.colors.dataTable.loading} />
                    </View>
                }
                {!isLoading &&
                    <>
                        <View style={{ gap: 10, zIndex: 2 }}>
                            <View style={styles.subTitleContainer}>
                                <MyAppText style={styles.subTitleText}>
                                    {translate('general_info')}
                                </MyAppText>
                            </View>
                            <View style={{
                                flexDirection: windowInfo.isMobile ? 'column' : 'row',
                                flexWrap: 'wrap',
                                zIndex: 2,
                                gap: 5,
                            }}>
                                {/* image blocks */}
                                <PersonFormPictures
                                    facePictures={facePictures}
                                    setDeleteFacePictureModalVisible={setDeleteFacePictureModalVisible}
                                    setFacePictureToDelete={setFacePictureToDelete}
                                    setImageZoom={setImageZoom}
                                    files={files}
                                    setFiles={setFiles}
                                    isSspWantedPerson={isSspWantedPerson}
                                />

                                {/* personal data  */}
                                <View style={{ gap: 5, flex: 1 }}>
                                    <PersonTypeDropdown
                                        value={personType}
                                        setValue={setPersonType}
                                        zIndex={3}
                                        disabled={!!route.params?.id}
                                        includeEmptyItem={true}
                                        required={true}
                                        type='write'
                                    />
                                    <FormInput
                                        label={translate('name')}
                                        value={name}
                                        onChangeText={setName}
                                        invalid={() => name === ''}
                                        required={true}
                                    />
                                    <View style={{ height: 40, gap: 5, flexDirection: 'row', alignItems: 'center' }}>
                                        <MyAppText style={{
                                            fontSize: 16,
                                            height: 25,
                                            color: theme.colors.labelColor,
                                        }}>{translate('active')}</MyAppText>
                                        <Switch
                                            trackColor={{ false: '#767577', true: '#81b0ff' }}
                                            thumbColor={isActive ? '#f5dd4b' : '#f4f3f4'}
                                            onValueChange={(value) => {
                                                setIsActive(value);
                                            }}
                                            value={isActive}
                                            style={{ alignSelf: 'center' }}
                                        />
                                    </View>
                                    {!isSspWantedPerson &&
                                        <>
                                            <FormInput
                                                label={translate('nickname')}
                                                value={nickname}
                                                onChangeText={setNickname}
                                                invalid={() => false}
                                            />
                                            <MyCpfInput
                                                value={document}
                                                onChangeText={(value) => {
                                                    setDocument(value);
                                                    if (value === '') {
                                                        setCpfInvalid(false);
                                                    }
                                                }}
                                                viewStyle={{ flexGrow: 1, minWidth: 200 }}
                                                isInvalid={isCpfInvalid}
                                                setIsInvalid={setCpfInvalid}
                                            />
                                            <FormInput
                                                label={translate('personRegister')}
                                                value={register}
                                                onChangeText={setRegister}
                                                invalid={() => false}
                                            />
                                            <FormInput
                                                label={translate('state')}
                                                value={state}
                                                onChangeText={setState}
                                                invalid={() => false}
                                            />
                                            <FormInput
                                                label={translate('city')}
                                                value={city}
                                                onChangeText={setCity}
                                                invalid={() => false}
                                            />
                                        </>
                                    }
                                    <TagsDropdown
                                        value={personMarkers}
                                        setValue={setPersonMarkers}
                                        zIndex={3}
                                        readOnly={isSspWantedPerson}
                                    />
                                </View>
                            </View>
                        </View>
                        {
                            personType == PersonType.WANTED && !isSspWantedPerson && <View style={{ gap: 10 }}>
                                <View style={styles.subTitleContainer}>
                                    <MyAppText style={styles.subTitleText}>
                                        {translate('arrestWarrants')}
                                    </MyAppText>
                                </View>
                                <View style={{ flexDirection: windowInfo.isMobile ? 'column' : 'row', gap: 10 }}>
                                    <View style={{ flexDirection: 'column', flex: 1, gap: 10 }}>
                                        <FormInput
                                            label={translate('situation')}
                                            value={situation}
                                            onChangeText={setSituation}
                                            invalid={() => false}
                                        />
                                        <FormInput
                                            label={translate('arrestWarrantNumber')}
                                            value={arrestWarrantNumber}
                                            onChangeText={setArrestWarrantNumber}
                                            invalid={() => false}
                                        />
                                        <FormInput
                                            label={translate('shippingDate')}
                                            value={shippingDate}
                                            onChangeText={setShippingDate}
                                            invalid={() => false}
                                        />
                                        <FormInput
                                            label={translate('expirationDate')}
                                            value={expirationDate}
                                            onChangeText={setExpirationDate}
                                            invalid={() => false}
                                        />
                                        <FormInput
                                            label={translate('lawsuitNumber')}
                                            value={lawsuitNumber}
                                            onChangeText={setLawsuitNumber}
                                            invalid={() => false}
                                        />
                                    </View>
                                    <View style={{ flexDirection: 'column', flex: 1, gap: 10 }}>
                                        <FormInput
                                            label={translate('prisonKind')}
                                            value={prisonKind}
                                            onChangeText={setPrisonKind}
                                            invalid={() => false}
                                        />
                                        <FormInput
                                            label={translate('magistrate')}
                                            value={magistrate}
                                            onChangeText={setMagistrate}
                                            invalid={() => false}
                                        />
                                        <FormInput
                                            label={translate('issuingBody')}
                                            value={issuingBody}
                                            onChangeText={setIssuingBody}
                                            invalid={() => false}
                                        />
                                        <FormInput
                                            label={translate('county')}
                                            value={lawsuitCity}
                                            onChangeText={setLawsuitCity}
                                            invalid={() => false}
                                        />
                                    </View>
                                </View>
                            </View>
                        }
                        {!isSspWantedPerson &&
                            <View style={{ gap: 10 }}>
                                <View style={styles.subTitleContainer}>
                                    <MyAppText style={styles.subTitleText}>
                                        {translate('additionalComments')}
                                    </MyAppText>
                                </View>
                                <FormInput
                                    value={comment}
                                    onChangeText={setComment}
                                    invalid={() => false}
                                    multiline={true}
                                    numberOfLines={5}
                                    maxLength={16777216}
                                />
                            </View>
                        }
                    </>
                }
                {/* Zoom image modal */}
                <Modal
                    animationType='fade'
                    transparent={true}
                    visible={imageZoom !== undefined}
                    onRequestClose={() => setImageZoom(undefined)}>
                    <View style={styles.modalContainer}>
                        <View style={{
                            backgroundColor: theme.colors.container.background,
                            borderRadius: 8,
                            padding: 35,
                            alignItems: 'center',
                            width: 600,
                            marginLeft: (windowInfo.width / 2) - 300,
                            marginTop: (windowInfo.height / 2) - 300,
                        }}>
                            <TouchableOpacity
                                style={{ margin: 10, position: 'absolute', top: 10, left: 10 }}
                                onPress={() => setImageZoom(undefined)}>
                                <FontAwesomeIcon icon={faTimes} fontSize={15} color={theme.colors.textColor} />
                            </TouchableOpacity>
                            <Image
                                style={{
                                    height: 600,
                                    width: '100%',
                                    borderRadius: 8
                                }}
                                resizeMode='contain'
                                source={{
                                    uri: imageZoom?.imageUrl
                                }}
                            />
                        </View>
                    </View>
                </Modal>
            </ScrollView>
        </Card>

        <DeleteModal
            setModalVisible={setDeleteModalVisible}
            isModalVisible={isDeleteModalVisible}
            itemName={name}
            onSubmit={() => deletePerson(route.params?.id || 0)}
        />

        <DeleteModal
            setModalVisible={setDeleteFacePictureModalVisible}
            isModalVisible={isDeleteFacePictureModalVisible}
            onSubmit={() => facePictureToDelete && deleteFacePicture(facePictureToDelete)}
        />
    </Container>;
}


const stylesheet = createStyleSheet(theme => ({
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199'
    },
    subTitleContainer: {
        borderBottomWidth: 1,
        borderColor: theme.colors.labelColor,
    },
    subTitleText: {
        color: theme.colors.labelColor,
        fontWeight: 'bold',
        fontSize: 16
    },
}));
